import React, { useState, useEffect } from "react";
import {
  CheckSquare, Sliders, Shield, Plus, Edit, Trash2,
  ArrowUpRight, DollarSign, Settings, Calendar, Clock,
  Filter, Search, Star, AlertCircle, X, Users,
  ChevronDown, ArrowRight, FileText, PercentIcon,
  LockIcon, UnlockIcon, Check, Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import PaymentMilestoneModal from "./PaymentMilestoneModal";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  fetchPaymentMilestones,
  deletePaymentMilestone,
  clearPaymentMilestonesError
} from "../../redux/Slices/PaymentMilestoneSlice";
import { PaymentMilestoneStatusEnum, PaymentMilestoneCategoryTypeEnum } from "../../modules/shared/model";

const PaymentMilestones = () => {
  const dispatch = useAppDispatch();
  const { 
    milestones, 
    milestonesLoading, 
    milestonesError, 
    milestonesTotalCount,
    milestonesHasNextPage,
    milestonesHasPreviousPage,
    milestonesCurrentPage,
    milestonesLimit
  } = useAppSelector((state) => state.paymentMilestones);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Load payment milestones on component mount and when pagination changes
  useEffect(() => {
    console.log("Fetching milestones with:", { page: currentPage, limit: itemsPerPage });
    dispatch(fetchPaymentMilestones({ page: currentPage, limit: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  // Clear errors when component mounts
  useEffect(() => {
    dispatch(clearPaymentMilestonesError());
  }, [dispatch]);

  const handleDeleteMilestone = async (milestoneId: number) => {
    if (window.confirm("Are you sure you want to delete this payment milestone?")) {
      try {
        await dispatch(deletePaymentMilestone(milestoneId)).unwrap();
        // Refresh the milestones list
        dispatch(fetchPaymentMilestones({ page: currentPage, limit: itemsPerPage }));
      } catch (error) {
        console.error("Error deleting payment milestone:", error);
      }
    }
  };

  const handleMilestoneCreated = () => {
    // Refresh the milestones list after successful creation
    dispatch(fetchPaymentMilestones({ page: currentPage, limit: itemsPerPage }));
  };

  const handleEditMilestone = (milestone: any) => {
    setShowEditModal(milestone);
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    console.log("Changing page to:", page);
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    console.log("Changing items per page to:", items);
    setItemsPerPage(items);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Debug logging
  console.log("Milestones data:", {
    milestones: milestones.length,
    milestonesTotalCount,
    milestonesHasNextPage,
    milestonesHasPreviousPage,
    milestonesCurrentPage,
    currentPage,
    itemsPerPage
  });

  // Use server-side pagination data
  const totalPages = Math.ceil((milestonesTotalCount || 0) / itemsPerPage);

  const getStatusName = (status: any) => {
    switch (Number(status?.id)) {
      case PaymentMilestoneStatusEnum.Active: return "Active";
      case PaymentMilestoneStatusEnum.Inactive: return "Inactive";
      case PaymentMilestoneStatusEnum.Draft: return "Draft";
      default: return "Active";
    }
  };

  const getStatusColor = (status: any) => {
    switch (Number(status?.id)) {
      case PaymentMilestoneStatusEnum.Active: return "bg-green-100 text-green-700";
      case PaymentMilestoneStatusEnum.Inactive: return "bg-red-100 text-red-700";
      case PaymentMilestoneStatusEnum.Draft: return "bg-yellow-100 text-yellow-700";
      default: return "bg-green-100 text-green-700";
    }
  };

  const getCategoryName = (category: any) => {
    switch (Number(category?.id)) {
      case PaymentMilestoneCategoryTypeEnum.LocalServices: return "Local Services";
            case PaymentMilestoneCategoryTypeEnum.Professional: return "Professional";
            case PaymentMilestoneCategoryTypeEnum.International: return "International";
            case PaymentMilestoneCategoryTypeEnum.Emergency: return "Emergency";
      default: return "Local Services";
    }
  };

  // Filter milestones based on search and category
  const filteredMilestones = milestones.filter(milestone => {
    const matchesSearch = milestone.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || 
      (selectedCategory === "local" && Number(milestone.categoryType?.id) === PaymentMilestoneCategoryTypeEnum.LocalServices) ||
      (selectedCategory === "emergency" && Number(milestone.categoryType?.id) === PaymentMilestoneCategoryTypeEnum.Emergency) ||
      (selectedCategory === "professional" && Number(milestone.categoryType?.id) === PaymentMilestoneCategoryTypeEnum.Professional);
    
    return matchesSearch && matchesCategory;
  });

  const MilestoneCard = ({ milestone }) => (
    <Card className="p-6">
      <div className="flex justify-between items-start"   style={{ textAlign: "left" }}>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{milestone.name}</h3>
            <Badge variant={undefined} className={getStatusColor(milestone.status)}>
              {getStatusName(milestone.status)}
            </Badge>
          </div>
          <p className="text-gray-600 mt-1">{milestone.categoryType ? getCategoryName(milestone.categoryType) : "No Category"}</p>
          {milestone.user && (
            <p className="text-gray-500 text-sm mt-1">
              User: {milestone.user.firstName} {milestone.user.lastName} ({milestone.user.email})
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditMilestone(milestone)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
            onClick={() => handleDeleteMilestone(milestone.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Payment Stages */}
      <div className="mt-6"   style={{ textAlign: "left" }}>
        <h4 className="font-medium mb-4">Payment Stages</h4>
        <div className="space-y-4">
          {milestone.stages && milestone.stages.length > 0 ? (
            milestone.stages.map((stage, index) => (
              <div key={stage.id || index} className="flex items-center">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-sm text-blue-600 font-medium">
                      {index + 1}
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{stage.name}</span>
                    <Badge variant={undefined} className="bg-blue-100 text-blue-700">
                      {stage.percentage}%
                    </Badge>
                  </div>
                  <div className="ml-8 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Release Conditions</div>
                      <div className="text-sm">
                        {Array.isArray(stage.releaseCondition) ? (
                          <ul className="list-disc list-inside space-y-1">
                            {stage.releaseCondition.map((condition, idx) => (
                              <li key={idx}>{condition}</li>
                            ))}
                          </ul>
                        ) : (
                          <span>{stage.releaseCondition}</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Holding Period</div>
                      <div className="text-sm">{stage.holdingPeriod} hours</div>
                    </div>
                  </div>
                </div>
                <Badge variant="outline" className={
                  stage.refundable ? "border-green-200 text-green-700" : "border-red-200 text-red-700"
                }>
                  {stage.refundable ? "Refundable" : "Non-refundable"}
                </Badge>
              </div>
            ))
          ) : (
            <div className="text-gray-500 text-sm">No stages configured</div>
          )}
        </div>
      </div>

      {/* Settings */}
      <div className="mt-6 pt-6 border-t">
        <h4 className="font-medium mb-4">Settings</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Auto-Release</div>
            <Badge variant={undefined} className={milestone.autoReleaseSetting ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {milestone.autoReleaseSetting ? "Enabled" : "Disabled"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Client Approval</div>
            <Badge variant={undefined} className={milestone.clientApprovalSetting ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {milestone.clientApprovalSetting ? "Required" : "Optional"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Dispute Period</div>
            <Badge variant="outline">{milestone.disputePeriodSetting}</Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Escrow</div>
            <Badge variant={undefined} className={milestone.escrowEnabledSetting ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {milestone.escrowEnabledSetting ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"   style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-left">
                <h1 className="text-2xl font-bold text-gray-900">Payment Milestones</h1>
                <p className="text-gray-600 mt-1">Manage payment stages and release conditions</p>
              </div>
              <Button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Template
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Milestones</p>
                    <p className="text-2xl font-semibold mt-1">
                      {milestonesLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        milestones.filter(m => Number(m.status?.id) === PaymentMilestoneStatusEnum.Active).length
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Stages</p>
                    <p className="text-2xl font-semibold mt-1">
                      {milestonesLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        milestones.reduce((total, milestone) => total + (milestone.stages?.length || 0), 0)
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckSquare className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Milestones</p>
                    <p className="text-2xl font-semibold mt-1">
                      {milestonesLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        milestonesTotalCount || 0
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Categories</p>
                    <p className="text-2xl font-semibold mt-1">
                      {milestonesLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        new Set(milestones.map(m => m.categoryType?.id)).size
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Star className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search templates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="all">All Categories</option>
                  <option value="local">Local Services</option>
                  <option value="emergency">Emergency Services</option>
                  <option value="professional">Professional Services</option>
                </select>
              </div>
            </Card>

            {/* Error Display */}
            {milestonesError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  <p className="text-red-700">{milestonesError}</p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {milestonesLoading && milestones.length === 0 && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading payment milestones...</span>
              </div>
            )}

            {/* Milestone Templates */}
            {!milestonesLoading && (
              <div className="grid grid-cols-1 gap-6">
                {filteredMilestones.length > 0 ? (
                  filteredMilestones.map(milestone => (
                    <MilestoneCard key={milestone.id} milestone={milestone} />
                  ))
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No payment milestones found</h3>
                    <p className="text-gray-600 mb-4">
                      {searchQuery || selectedCategory !== "all" 
                        ? "Try adjusting your search or filter criteria."
                        : "Get started by creating your first payment milestone."
                      }
                    </p>
                    {!searchQuery && selectedCategory === "all" && (
                      <Button onClick={() => setShowAddModal(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create First Milestone
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {!milestonesLoading && filteredMilestones.length > 0 && (
              <Card className="p-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, milestonesTotalCount || 0)} of {milestonesTotalCount || 0} milestones
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Items per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="px-2 py-1 border rounded text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!milestonesHasPreviousPage}
                    >
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                        if (pageNum > totalPages) return null;
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className="w-8 h-8 p-0"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!milestonesHasNextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Modals */}
            <PaymentMilestoneModal
              isOpen={showAddModal}
              onClose={() => setShowAddModal(false)}
              onSuccess={handleMilestoneCreated}
            />

            <PaymentMilestoneModal
              isOpen={!!showEditModal}
              onClose={() => setShowEditModal(null)}
              onSuccess={handleMilestoneCreated}
              editMilestone={showEditModal}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentMilestones;